import axios from "axios";
import { message } from "antd";
import {
  downloadDeviceImportTemplateCsv,
  downloadHuifuAccountImportTemplateCsv,
  downloadHuifuBankCardImportTemplateCsv,
} from "../utils/exportUtils";

// 保留原来的 http 实例（完全不变！）
const http = axios.create({
  baseURL: "https://paizhao.yunjiaozu.com", // 保留原来的 baseURL
  timeout: 10000,
  headers: { "Content-Type": "application/json" },
});

// 添加全局标志，防止重复跳转
let isRedirectingToUnauthorized = false;

// 导出重置函数，供登录成功后调用
export const resetUnauthorizedFlag = () => {
  isRedirectingToUnauthorized = false;
};

// 请求拦截器
http.interceptors.request.use(
  (config) => {
    // 如果正在跳转到未授权页面，取消所有请求
    if (isRedirectingToUnauthorized) {
      return Promise.reject(new Error('正在跳转到登录页面'));
    }
    
    // 检查token是否还存在，如果不存在且不是登录接口，直接跳转
    const adminToken = localStorage.getItem("admin_token");
    if (!adminToken && !config.url.includes("/api/auth/admin-login")) {
      if (!isRedirectingToUnauthorized) {
        isRedirectingToUnauthorized = true;
        setTimeout(() => {
          window.location.replace('#/unauthorized');
        }, 0);
      }
      return Promise.reject(new Error('未登录'));
    }
    
    const adminAuthUrls =
      config.url.includes("/api/admin/") ||
      config.url.includes("/api/bills/") ||
      config.url.includes("/api/auth/admin-logout") ||
      config.url.includes("/api/mqtt/") ||
      config.url.includes("/api/devices/electric/upload-reading");
    if (adminAuthUrls) {
      if (adminToken) {
        config.headers["Authorization"] = `Bearer ${adminToken}`;
      }
    }
    
    // 如果是登录接口且成功，重置标志位
    if (config.url.includes("/api/auth/admin-login")) {
      isRedirectingToUnauthorized = false;
    }
    
    return config;
  },
  (err) => Promise.reject(err)
);

// 响应拦截器
http.interceptors.response.use(
  (res) => {
    // 如果响应成功，且是登录接口，重置标志位（允许后续请求）
    const url = res.config?.url || '';
    if (url.includes("/api/auth/admin-login") && res.data?.success) {
      isRedirectingToUnauthorized = false;
    }
    return res.data;
  },
  (err) => {
    const url = err.config?.url || '';
    const isLoginUrl = url && url.includes("/api/auth/admin-login");
    
    // 对于登录接口的错误，不显示message，让登录组件自己处理（显示Modal）
    if (isLoginUrl) {
      // 登录接口的错误直接返回，让登录组件处理
      // 确保错误对象包含响应数据，方便组件使用
      return Promise.reject(err);
    }
    
    // 检测 401：管理端主流程（/api/admin/*）仍视为登录失效；MQTT/抄表上传常因 Casbin 未放行返回 401，勿清 Token 误踢登录
    if (err.response?.status === 401) {
      const adminToken = localStorage.getItem("admin_token");
      const isMqttOrReadingUpload =
        url.includes("/api/mqtt/") ||
        url.includes("/api/devices/electric/upload-reading");
      const isBillApi =
        url.includes("/api/bills/") ||
        url.includes("/api/admin/mark-bill-paid") ||
        url.includes("/api/admin/revert-bill-unpaid") ||
        url.includes("/api/admin/bills/");

      if (adminToken && (isMqttOrReadingUpload || isBillApi)) {
        const hint =
          err.response?.data?.msg ||
          err.response?.data?.error ||
          "无权限或鉴权失败（请检查管理端账单路由/Casbin策略是否放行）";
        message.error(hint);
        return Promise.reject(err);
      }

      if (adminToken && !isRedirectingToUnauthorized) {
        isRedirectingToUnauthorized = true;
        localStorage.removeItem("admin_token");
        localStorage.removeItem("admin_username");
        setTimeout(() => {
          window.location.replace("#/unauthorized");
        }, 0);
        return Promise.reject(new Error("UNAUTHORIZED"));
      }
    }
    
    // 如果已经在跳转中，直接拒绝请求，不显示错误消息
    if (isRedirectingToUnauthorized) {
      return Promise.reject(err);
    }
    
    const errorMsg = err.response?.data?.error || err.response?.data?.msg || "接口请求失败";
    message.error(errorMsg);
    return Promise.reject(err);
  }
);

// 原有接口（保留不变）
export const adminLogin = (data) => http.post("/api/auth/admin-login", data);
export const adminLogout = () => http.post("/api/auth/admin-logout");
/** 管理端用户列表：分页 + keyword 搜索昵称/手机/openid + user_type + house_admin_only */
export const getUsers = (params = {}) => {
  const {
    page = 1,
    page_size = 20,
    keyword = "",
    user_type,
    house_admin_only,
  } = params;
  const q = { page, page_size };
  const kw = String(keyword || "").trim();
  if (kw) q.keyword = kw;
  if (user_type !== undefined && user_type !== null && user_type !== "") {
    q.user_type = user_type;
  }
  if (house_admin_only) q.house_admin_only = 1;
  return http.get("/api/admin/users", { params: q });
};

/** 单个用户（编辑页，避免拉全表） */
export const getAdminUserById = (id) => http.get(`/api/admin/users/${id}`);

/** 管理端：手动测试语音外呼（仅 voice_notify_test_phones 白名单用户）[已弃用] */
// export const adminTestVoiceCall = (userId, data) =>
//   http.post(`/api/admin/users/${userId}/test-voice-call`, data || {});

/** 管理端：补发低余额语音（仅 voice_notify_test_phones 白名单用户） */
export const adminResendUserVoiceLowBalance = (userId) =>
  http.post(`/api/admin/users/${userId}/resend-voice-low-balance`);

/** 管理端：重置低余额提醒标记（便于再次巡检/补发） */
export const adminResetUserVoiceLowBalanceFlag = (userId) =>
  http.post(`/api/admin/users/${userId}/reset-voice-low-balance-flag`);

/** 管理端：查询用户指定日语音队列接听结果（day: today | yesterday） */
export const adminQueryUserVoiceCallsToday = (userId, day = 'today') =>
  http.get(`/api/admin/users/${userId}/voice-calls-today`, {
    params: day === 'yesterday' ? { day: 'yesterday' } : {},
  });

/** 管理端：公共号池低电量话术测试（任意手机号，不走白名单） */
export const adminPublicPoolVoiceTest = (data) =>
  http.post('/api/admin/voice/public-pool-test', data || {});

/** 管理端：查询指定手机号今日公共号池测试记录 */
export const adminPublicPoolVoiceTestCallsToday = (phone) =>
  http.get('/api/admin/voice/public-pool-calls-today', { params: { phone } });

/** 管理端：专属外显低电量话术测试（外显号页面手填，不走白名单） */
export const adminExclusivePoolVoiceTest = (data) =>
  http.post('/api/admin/voice/exclusive-pool-test', data || {});

/** 管理端：查询指定手机号今日专属外显测试记录 */
export const adminExclusivePoolVoiceTestCallsToday = (phone) =>
  http.get('/api/admin/voice/exclusive-pool-calls-today', { params: { phone } });

/** 管理端：蓝牙电表预估预览（QK/NQK） */
export const adminBluetoothEstimatePreview = (data) =>
  http.post('/api/admin/voice/bluetooth-estimate-preview', data || {});

/** 管理端：蓝牙公共号池语音测试（仅 fang） */
export const adminBluetoothPublicPoolVoiceTest = (data) =>
  http.post('/api/admin/voice/bluetooth-public-pool-test', data || {});

export const adminBluetoothPublicPoolVoiceTestCallsToday = (phone) =>
  http.get('/api/admin/voice/bluetooth-public-pool-calls-today', { params: { phone } });

/** 管理端：蓝牙专属号池语音测试（仅 fang） */
export const adminBluetoothExclusivePoolVoiceTest = (data) =>
  http.post('/api/admin/voice/bluetooth-exclusive-pool-test', data || {});

export const adminBluetoothExclusivePoolVoiceTestCallsToday = (phone) =>
  http.get('/api/admin/voice/bluetooth-exclusive-pool-calls-today', { params: { phone } });

/** 管理端：蓝牙低电通知预估（批量，模拟正式巡检口径；设备多时可较慢） */
export const adminBluetoothLowBalanceNotifyPreview = (data) =>
  http.post('/api/admin/voice/bluetooth-low-balance-notify-preview', data || {}, {
    timeout: 120000,
  });

export const getAllHouses = () => http.get("/api/admin/houses");
export const getAllRooms = () => http.get("/api/admin/rooms");
export const adminChangePassword = (data) => http.post("/api/admin/create-or-reset", data);

/** 首页数据概览：服务端聚合（避免全量 houses/devices/bills/orders） */
export const getDashboardOverview = () => {
  return http.get("/api/admin/dashboard/overview").then((res) => {
    if (!res?.success) {
      throw new Error(res?.msg || res?.error || "加载概览失败");
    }
    return res.data || {};
  });
};

// 1. 修改：更新用户信息接口（适配后端新路由，传递昵称/手机号/角色）
export const updateUserInfo = (id, params) => {
  return http.put(`/api/admin/users/${id}`, params); // params 包含 nickname/phone/user_type
};


/** 新建虚拟收款用户（代收号，用于房源指定收账人） */
export const createVirtualCollectorUser = (data) => {
  return http.post('/api/admin/users/virtual-collector', data);
};

// 新增：删除用户接口（带日志）
export const deleteUser = (id) => {
  console.log("开始删除用户：ID=", id); // 打印要删除的ID
  return http.delete(`/api/admin/users/${id}`)
    .then(res => {
      console.log("删除用户成功响应：", res); // 打印成功响应
      return res;
    })
    .catch(err => {
      console.error("删除用户失败：", err); // 打印失败详情
      throw err; // 继续抛出错误，让上层处理
    });
};




// // 2. 新增：删除用户接口（适配后端删除路由）
// export const deleteUser = (id) => {
//   return http.delete(`/api/admin/users/${id}`);
// };

// （可选）保留原来的只改角色接口（如果需要单独改角色时用）
// export const updateUserRole = (id, userType) => {
//   return http.put(`/api/admin/users/${id}/role`, { user_type: userType });
// };



// 房源相关
// 1. 房源新增接口
export const addHouse = (params) => {
  return http.post("/api/admin/houses", params); // 传递房源名称/地址/房东ID等
};

// 2. 房源编辑接口
export const updateHouse = (id, params) => {
  return http.put(`/api/admin/houses/${id}`, params); // 传递要修改的字段
};

// 3. 单个房源删除接口
export const deleteHouse = (id) => {
  console.log("开始删除房源：ID=", id); // 打印要删除的ID
  return http.delete(`/api/admin/houses/${id}`)
    .then(res => {
      console.log("删除房源成功响应：", res); // 打印成功响应
      return res;
    })
    .catch(err => {
      console.error("删除房源失败：", err); // 打印失败详情
      throw err; // 继续抛出错误，让上层处理
    });
};

// 4. 批量删除房源接口
export const batchDeleteHouses = (params) => {
  console.log("开始批量删除房源：参数=", params);
  return http.post("/api/admin/houses/batch-delete", params)
    .then(res => {
      console.log("批量删除房源成功响应：", res);
      return res;
    })
    .catch(err => {
      console.error("批量删除房源失败：", err);
      throw err;
    });
};







// // 房间相关接口
// export const getAllRooms = () => {
//   return http.get('/api/admin/rooms');
// };

export const addRoom = (data) => {
  console.log("新增房间：参数=", data);
  return http.post(`/api/admin/rooms`, data)
    .then(res => {
      console.log("新增房间成功响应：", res);
      return res;
    })
    .catch(err => {
      console.error("新增房间失败：", err);
      throw err;
    });
};

export const updateRoom = (id, data) => {
  console.log("编辑房间：ID=", id, "参数=", data);
  return http.put(`/api/admin/rooms/${id}`, data)
    .then(res => {
      console.log("编辑房间成功响应：", res);
      return res;
    })
    .catch(err => {
      console.error("编辑房间失败：", err);
      throw err;
    });
};

export const deleteRoom = (id) => {
  console.log("删除房间：ID=", id);
  return http.delete(`/api/admin/rooms/${id}`)
    .then(res => {
      console.log("删除房间成功响应：", res);
      return res;
    })
    .catch(err => {
      console.error("删除房间失败：", err);
      throw err;
    });
};

// 新增：批量删除房间接口（核心：自动转数字ID）
export const batchDeleteRooms = (roomIds) => {
  console.log('===== 批量删除房间：原始ID列表 =====', roomIds);
  
  // 1. 参数校验
  if (!roomIds || !Array.isArray(roomIds) || roomIds.length === 0) {
    const err = new Error('房间ID列表不能为空，且必须为数组');
    err.response = { status: 400, data: { error: err.message } };
    console.error('批量删除房间：参数错误', err);
    return Promise.reject(err);
  }

  // 2. 过滤+转数字（关键：避免字符串ID导致后端解析失败）
  const validRoomIds = roomIds.filter(id => {
    const numId = Number(id);
    return id && !isNaN(numId) && numId > 0;
  }).map(id => Number(id));

  if (validRoomIds.length === 0) {
    const err = new Error('没有有效的房间ID，请检查选中的记录');
    err.response = { status: 400, data: { error: err.message } };
    console.error('批量删除房间：有效ID为空', err);
    return Promise.reject(err);
  }

  console.log('===== 批量删除房间：过滤后数字ID =====', validRoomIds);

  // 3. 调用后端批量删除接口
  return http.post('/api/admin/rooms/batch-delete', {
    roomIds: validRoomIds // 传数字数组，和后端uint64匹配
  })
  .then(res => {
    console.log("批量删除房间成功响应：", res);
    return res;
  })
  .catch(err => {
    console.error("批量删除房间失败：", err);
    throw err;
  });
};










// 账单相关接口（分页 + search；opts.all=1 时导出用，最多 2 万条）
export const getAllBills = (search = '', opts = {}) => {
  const page = Number(opts.page) > 0 ? Number(opts.page) : 1;
  const page_size = Number(opts.page_size) > 0 ? Number(opts.page_size) : 50;
  const params = { search: search || "", page, page_size };
  if (opts.all) params.all = 1;
  return http
    .get("/api/admin/bills", { params })
    .then((res) => {
      console.log("查询账单成功响应：", res);
      return res;
    })
    .catch((err) => {
      console.error("查询账单失败：", err);
      throw err;
    });
};

export const sendBillMpRentNotify = (billId) => {
  const id = String(billId ?? '').trim();
  if (!id) return Promise.reject(new Error('账单ID不能为空'));
  return http.post(`/api/admin/bills/${id}/send-mp-rent-notify`);
};

export const resetBillMpRentNotifyFlag = (billId) => {
  const id = String(billId ?? '').trim();
  if (!id) return Promise.reject(new Error('账单ID不能为空'));
  return http.post(`/api/admin/bills/${id}/reset-mp-rent-notify-flag`);
};

export const deleteBill = (billId) => {
  console.log('===== API层：开始调用删除账单接口 =====');
  console.log('请求参数 - 账单ID：', billId, '类型：', typeof billId);
  
  if (billId === undefined || billId === null || billId === '') {
    const err = new Error('账单ID不能为空');
    err.response = { status: 400, data: { error: '账单ID不能为空' } };
    console.error('API层：账单ID为空，拒绝请求');
    return Promise.reject(err);
  }
  
  // 🔥 已改对：URL是 /api/admin/delete-bill/${billId}
  const requestUrl = `/api/admin/delete-bill/${billId}`;
  console.log('最终请求URL：', requestUrl);
  
  return http.delete(requestUrl)
    .then(res => {
      console.log('API层：删除账单接口响应成功：', res);
      return res;
    })
    .catch(err => {
      console.error('API层：删除账单接口响应失败（完整信息）：', {
        status: err.response?.status,
        data: err.response?.data,
        config: err.config,
        message: err.message,
        requestUrl: err.config?.url
      });
      throw err;
    });
};

export const getBillDetail = (billId) => {
  if (billId === undefined || billId === null || billId === '') {
    const err = new Error('账单ID不能为空');
    err.response = { status: 400, data: { error: '账单ID不能为空' } };
    return Promise.reject(err);
  }

  return http.get(`/api/admin/bills/${billId}`)
    .then((res) => {
      console.log('获取账单详情成功（admin路由）：', res);
      return res;
    })
    .catch((err) => {
      if ([400, 404].includes(err?.response?.status)) {
        return http.get(`/api/bills/${billId}`)
          .then((res) => {
            console.log('获取账单详情成功（兼容路由）：', res);
            return res;
          });
      }
      console.error('获取账单详情失败：', err);
      throw err;
    });
};

export const markBillAsPaid = (billId) => {
  if (billId === undefined || billId === null || billId === '') {
    const err = new Error('账单ID不能为空');
    err.response = { status: 400, data: { error: '账单ID不能为空' } };
    return Promise.reject(err);
  }

  // 后端 binding 要求 PaySource；与账单列表「支付来源」一致：2=房东/管理员标记已收款
  const payload = { bill_id: Number(billId), pay_source: 2 };
  return http.post(`/api/admin/mark-bill-paid/${billId}`, payload)
    .then((res) => {
      console.log('标记账单支付成功（admin路由）：', res);
      return res;
    })
    .catch((err) => {
      console.error('标记账单支付失败：', err);
      throw err;
    });
};

export const revertBillToUnpaid = (billId) => {
  if (billId === undefined || billId === null || billId === '') {
    const err = new Error('账单ID不能为空');
    err.response = { status: 400, data: { error: '账单ID不能为空' } };
    return Promise.reject(err);
  }
  return http.post(`/api/admin/revert-bill-unpaid/${billId}`)
    .then((res) => {
      console.log('恢复账单未支付成功（admin路由）：', res);
      return res;
    })
    .catch((err) => {
      console.error('恢复账单未支付失败：', err);
      throw err;
    });
};


// 新增：批量删除账单接口
export const batchDeleteBills = (billIds) => {
  console.log('===== API层：开始调用批量删除账单接口 =====');
  console.log('请求参数 - 账单ID列表：', billIds, '类型：', typeof billIds);
  
  // 参数校验
  if (!billIds || !Array.isArray(billIds) || billIds.length === 0) {
    const err = new Error('账单ID列表不能为空，且必须为数组');
    err.response = { status: 400, data: { error: '账单ID列表不能为空，且必须为数组' } };
    console.error('API层：账单ID列表参数错误，拒绝请求');
    return Promise.reject(err);
  }
  
  // 过滤空值和非数字ID
  // const validBillIds = billIds.filter(id => {
  //   const numId = Number(id);
  //   return id && !isNaN(numId) && numId > 0;
  // });
  // 只改这一段（批量删除API里）
const validBillIds = billIds.filter(id => {
  const numId = Number(id);
  return id && !isNaN(numId) && numId > 0;
}).map(id => Number(id)); // 新增这行：把字符串转成数字
  
  if (validBillIds.length === 0) {
    const err = new Error('没有有效的账单ID，请检查选中的记录');
    err.response = { status: 400, data: { error: '没有有效的账单ID，请检查选中的记录' } };
    console.error('API层：有效账单ID为空，拒绝请求');
    return Promise.reject(err);
  }
  
  console.log('过滤后的有效账单ID：', validBillIds);
  
  // 调用批量删除接口
  return http.post('/api/admin/batch-delete-bills', {
    billIds: validBillIds
  })
  .then(res => {
    console.log('API层：批量删除账单接口响应成功：', res);
    return res;
  })
  .catch(err => {
    console.error('API层：批量删除账单接口响应失败（完整信息）：', {
      status: err.response?.status,
      data: err.response?.data,
      config: err.config,
      message: err.message,
      requestUrl: err.config?.url
    });
    throw err;
  });
};








// 设备管理相关接口
export const getAllDevices = (deviceType = '') => {
  console.log("查询所有设备，设备类型：", deviceType);
  return http.get('/api/admin/devices', {
    params: {
      device_type: deviceType
    }
  })
  .then(res => {
    console.log("查询设备成功响应：", res);
    return res;
  })
  .catch(err => {
    console.error("查询设备失败：", err);
    throw err;
  });
};

/** 电表管理：服务端分页 + keyword 全库搜索（不传 page 时仍走全量，水表/门锁不受影响） */
export const getElectricMetersPage = (params = {}) => {
  const {
    page = 1,
    page_size = 20,
    keyword = '',
    match_mode = 'fuzzy',
    search_scope = '',
    location_keyword = '',
    location_match_mode = 'fuzzy',
    location_scope = '',
    room_bind_status = 'all',
  } = params;
  const q = {
    device_type: 1,
    page,
    page_size,
  };
  const kw = String(keyword || '').trim();
  if (kw) q.keyword = kw;
  q.match_mode = match_mode === 'exact' ? 'exact' : 'fuzzy';
  const scope = String(search_scope || '').trim();
  if (scope && scope !== 'all') q.search_scope = scope;
  const locKw = String(location_keyword || '').trim();
  if (locKw) q.location_keyword = locKw;
  q.location_match_mode = location_match_mode === 'exact' ? 'exact' : 'fuzzy';
  const locScope = String(location_scope || '').trim();
  if (locScope && locScope !== 'all') q.location_scope = locScope;
  const roomBind = String(room_bind_status || '').trim();
  if (roomBind && roomBind !== 'all') q.room_bind_status = roomBind;
  return http.get('/api/admin/devices', {
    params: q,
    timeout: 60000,
  });
};

/** 电表列表当前页：按设备 ID 批量查最新 RX 主题（仅 4G；查 mqtt_heartbeat_raw） */
export const fetchDeviceLatestRxTopics = (deviceIds = []) => {
  const ids = Array.isArray(deviceIds)
    ? deviceIds.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0)
    : [];
  if (ids.length === 0) {
    return Promise.resolve({ success: true, data: {} });
  }
  return http.post('/api/admin/devices/latest-rx-topics', { device_ids: ids }, { timeout: 30000 });
};

export const addDevice = (data) => {
  console.log("新增设备：参数=", data);
  return http.post('/api/admin/devices', data)
    .then(res => {
      console.log("新增设备成功响应：", res);
      return res;
    })
    .catch(err => {
      console.error("新增设备失败：", err);
      throw err;
    });
};

export const updateDevice = (id, data) => {
  console.log("编辑设备：ID=", id, "参数=", data);
  return http.put(`/api/admin/devices/${id}`, data)
    .then(res => {
      console.log("编辑设备成功响应：", res);
      return res;
    })
    .catch(err => {
      console.error("编辑设备失败：", err);
      throw err;
    });
};

export const deleteDevice = (id) => {
  console.log("删除设备：ID=", id);
  return http.delete(`/api/admin/devices/${id}`)
    .then(res => {
      console.log("删除设备成功响应：", res);
      return res;
    })
    .catch(err => {
      console.error("删除设备失败：", err);
      throw err;
    });
};

export const batchDeleteDevices = (deviceIds) => {
  console.log('批量删除设备：ID列表=', deviceIds);
  
  // 参数校验
  if (!deviceIds || !Array.isArray(deviceIds) || deviceIds.length === 0) {
    const err = new Error('设备ID列表不能为空，且必须为数组');
    err.response = { status: 400, data: { error: err.message } };
    console.error('批量删除设备：参数错误', err);
    return Promise.reject(err);
  }

  // 过滤+转数字
  const validDeviceIds = deviceIds.filter(id => {
    const numId = Number(id);
    return id && !isNaN(numId) && numId > 0;
  }).map(id => Number(id));

  if (validDeviceIds.length === 0) {
    const err = new Error('没有有效的设备ID，请检查选中的记录');
    err.response = { status: 400, data: { error: err.message } };
    console.error('批量删除设备：有效ID为空', err);
    return Promise.reject(err);
  }

  return http.post('/api/admin/devices/batch-delete', {
    device_ids: validDeviceIds
  })
  .then(res => {
    console.log("批量删除设备成功响应：", res);
    return res;
  })
  .catch(err => {
    console.error("批量删除设备失败：", err);
    throw err;
  });
};

export const getDeviceDetail = (id) => {
  console.log("查询设备详情：ID=", id);
  return http.get(`/api/admin/devices/${id}`)
    .then(res => {
      console.log("查询设备详情成功响应：", res);
      return res;
    })
    .catch(err => {
      console.error("查询设备详情失败：", err);
      throw err;
    });
};

/** PC 管理端：YJ 副表当前表号 → 最新心跳 RX 主题 → 反查主表 */
export const lookupMainMeterBySubMeter = (meterNo) => {
  return http.get('/api/mqtt/main-meter-lookup', {
    params: { meter_no: String(meterNo || '').trim() },
  });
};

/** 按表号/MAC 查 MQTT rx/tx 主题；档位由服务端 device_info 决定，extra 无需再传 mapping_fanout_tier */
export const getMqttMappingByMeter = (meterNo, extra = {}) => {
  const params = { meter_no: String(meterNo || "").trim(), ...extra };
  Object.keys(params).forEach((k) => {
    if (params[k] === undefined || params[k] === null || params[k] === "") delete params[k];
  });
  return http.get("/api/mqtt/mapping/by-meter", { params });
};

/** 通过表号映射下发 MQTT 负载（与小程序远程抄表/充值一致） */
export const mqttPublishByMeter = (body) => {
  return http.post("/api/mqtt/publish-by-meter", body);
};

/** 向指定 MQTT 主题下发指令（测试弹窗手动填 tx 主题时使用） */
export const mqttPublish = (body) => {
  return http.post("/api/mqtt/publish", body);
};

/** 读取某主题最新一条缓存消息（抄表轮询用，type 建议 raw）；opts 可含 meter_no、op、sent_at，与小程序一致以启用同表多 rx 聚合 */
export const getMqttLatestMessage = (topic, type = "raw", opts = {}) => {
  const params = { topic, type, ...opts };
  Object.keys(params).forEach((k) => {
    if (params[k] === undefined || params[k] === null || params[k] === "") delete params[k];
  });
  return http.get("/api/mqtt/message", { params });
};

/** 抄表结果回写设备表（电表/水表共用接口） */
export const uploadDeviceReading = (body) => {
  return http.post("/api/devices/electric/upload-reading", body);
};

// 下载设备批量导入模板（本地生成 CSV，含 IMEI、所属厂家，与后端导入列顺序一致；不依赖线上旧模板接口）
export const downloadDeviceTemplate = async () => {
  downloadDeviceImportTemplateCsv();
  return { success: true };
};

// 批量导入设备（上传CSV文件）
export const batchImportDevices = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  return http.post('/api/admin/devices/batch-import', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
    .then(res => {
      console.log("批量导入成功响应：", res);
      return res;
    })
    .catch(err => {
      console.error("批量导入失败：", err);
      throw err;
    });
};

// 订单管理相关接口
export const getAllOrders = (search = '', page = 1, pageSize = 20) => {
  console.log("查询订单，搜索关键词：", search, "页码：", page, "每页数量：", pageSize);
  return http.get('/api/admin/orders', {
    params: {
      search: search,
      page: page,
      page_size: pageSize
    }
  })
  .then(res => {
    console.log("查询订单成功响应：", res);
    return res;
  })
  .catch(err => {
    console.error("查询订单失败：", err);
    throw err;
  });
};

// 操作记录（管理端）
export const getAdminOperationLogs = (deviceType, search = '', page = 1, pageSize = 20) => {
  return http.get('/api/admin/operation-logs', {
    params: {
      device_type: deviceType,
      search,
      page,
      page_size: pageSize,
    },
  });
};

export const getAdminLockLogs = (search = '', page = 1, pageSize = 20) => {
  return http.get('/api/admin/lock-logs', {
    params: {
      search,
      page,
      page_size: pageSize,
    },
  });
};

export const getAdminVoiceNotifyOrders = (search = '', page = 1, pageSize = 20, payMonth = '') => {
  const params = { search, page, page_size: pageSize };
  if (payMonth) {
    params.pay_month = payMonth;
  }
  return http.get('/api/admin/voice-notify-orders', { params });
};

export const getAdminVoiceNotifySubscriptions = (search = '', page = 1, pageSize = 20) => {
  return http.get('/api/admin/voice-notify-subscriptions', {
    params: { search, page, page_size: pageSize },
  });
};

export const getOrderDetail = (orderId) => {
  console.log("查询订单详情：ID=", orderId);
  return http.get(`/api/admin/orders/${orderId}`)
    .then(res => {
      console.log("查询订单详情成功响应：", res);
      return res;
    })
    .catch(err => {
      console.error("查询订单详情失败：", err);
      throw err;
    });
};

/** 管理端修改电表订单购电次数快照、是否比对购电次数等（PATCH /api/admin/orders/:id/recharge-meta） */
export const patchAdminOrderRechargeMeta = (orderId, body) => {
  const id = String(orderId || '').trim();
  if (!id) return Promise.reject(new Error('订单ID不能为空'));
  return http
    .patch(`/api/admin/orders/${id}/recharge-meta`, body || {})
    .then((res) => res)
    .catch((err) => {
      console.error('管理端更新订单购电次数失败：', err);
      throw err;
    });
};

/** 管理端手动改订单支付状态 order_status（1=支付成功，2=未支付/失败；电/水费订单） */
export const updateAdminOrderPaymentStatus = (orderId, body) => {
  const id = String(orderId || '').trim();
  if (!id) {
    return Promise.reject(new Error('订单ID不能为空'));
  }
  return http
    .post(`/api/admin/orders/${id}/payment-status`, body || {})
    .then((res) => res)
    .catch((err) => {
      console.error('管理端更新订单支付状态失败：', err);
      throw err;
    });
};

/** 管理端汇付查单并同步支付状态（需订单已落库 pay_channel_* 或 body 传 huifu_id/org_hf_seq_id） */
export const confirmAdminOrderPayment = (orderId, body) => {
  const id = String(orderId || '').trim();
  if (!id) {
    return Promise.reject(new Error('订单ID不能为空'));
  }
  return http
    .post(`/api/admin/orders/${id}/confirm-payment`, body || {})
    .then((res) => res)
    .catch((err) => {
      console.error('管理端汇付查单同步支付状态失败：', err);
      throw err;
    });
};

/** 管理端手动改充值到账状态（与小程序补单回写同一套字段；仅已支付电/水费订单） */
export const updateAdminOrderRechargeResult = (orderId, body) => {
  const id = String(orderId || '').trim();
  if (!id) {
    return Promise.reject(new Error('订单ID不能为空'));
  }
  return http
    .post(`/api/admin/orders/${id}/recharge-result`, body || {})
    .then((res) => res)
    .catch((err) => {
      console.error('管理端更新充值结果失败：', err);
      throw err;
    });
};

export const deleteOrder = (orderId) => {
  console.log('===== API层：开始调用删除订单接口 =====');
  console.log('请求参数 - 订单ID：', orderId, '类型：', typeof orderId);
  
  if (!orderId || orderId === '') {
    const err = new Error('订单ID不能为空');
    err.response = { status: 400, data: { error: '订单ID不能为空' } };
    console.error('API层：订单ID为空，拒绝请求');
    return Promise.reject(err);
  }
  
  const requestUrl = `/api/admin/orders/${orderId}`;
  console.log('最终请求URL：', requestUrl);
  
  return http.delete(requestUrl)
    .then(res => {
      console.log('API层：删除订单接口响应成功：', res);
      return res;
    })
    .catch(err => {
      console.error('API层：删除订单接口响应失败（完整信息）：', {
        status: err.response?.status,
        data: err.response?.data,
        config: err.config,
        message: err.message,
        requestUrl: err.config?.url
      });
      throw err;
    });
};

export const batchDeleteOrders = (orderIds) => {
  console.log('===== API层：开始调用批量删除订单接口 =====');
  console.log('请求参数 - 订单ID列表：', orderIds, '类型：', typeof orderIds);
  
  // 参数校验
  if (!orderIds || !Array.isArray(orderIds) || orderIds.length === 0) {
    const err = new Error('订单ID列表不能为空，且必须为数组');
    err.response = { status: 400, data: { error: '订单ID列表不能为空，且必须为数组' } };
    console.error('API层：订单ID列表参数错误，拒绝请求');
    return Promise.reject(err);
  }
  
  // 过滤空值和非数字ID，并转换为数字
  const validOrderIds = orderIds.filter(id => {
    const numId = Number(id);
    return id && !isNaN(numId) && numId > 0;
  }).map(id => Number(id));
  
  if (validOrderIds.length === 0) {
    const err = new Error('没有有效的订单ID，请检查选中的记录');
    err.response = { status: 400, data: { error: '没有有效的订单ID，请检查选中的记录' } };
    console.error('API层：有效订单ID为空，拒绝请求');
    return Promise.reject(err);
  }
  
  console.log('过滤后的有效订单ID：', validOrderIds);
  
  // 调用批量删除接口
  return http.post('/api/admin/orders/batch-delete', {
    order_ids: validOrderIds
  })
  .then(res => {
    console.log('API层：批量删除订单接口响应成功：', res);
    return res;
  })
  .catch(err => {
    console.error('API层：批量删除订单接口响应失败（完整信息）：', {
      status: err.response?.status,
      data: err.response?.data,
      config: err.config,
      message: err.message,
      requestUrl: err.config?.url
    });
    throw err;
  });
};










// 分账管理相关接口
// 获取平台分账配置
export const getPlatformSplitConfig = () => {
  console.log("查询平台分账配置");
  return http.get('/api/admin/split-config/platform')
    .then(res => {
      console.log("查询平台分账配置成功响应：", res);
      return res;
    })
    .catch(err => {
      console.error("查询平台分账配置失败：", err);
      throw err;
    });
};

// 更新平台分账配置
export const updatePlatformSplitConfig = (params) => {
  console.log("更新平台分账配置：", params);
  return http.post('/api/admin/split-config/platform', params)
    .then(res => {
      console.log("更新平台分账配置成功响应：", res);
      return res;
    })
    .catch(err => {
      console.error("更新平台分账配置失败：", err);
      throw err;
    });
};

// 获取房源分账配置列表
export const getHouseSplitConfigList = (search = '', page = 1, pageSize = 20) => {
  console.log("查询房源分账配置列表，搜索关键词：", search, "页码：", page, "每页数量：", pageSize);
  return http.get('/api/admin/split-config/houses', {
    params: {
      search: search,
      page: page,
      page_size: pageSize
    }
  })
    .then(res => {
      console.log("查询房源分账配置列表成功响应：", res);
      return res;
    })
    .catch(err => {
      console.error("查询房源分账配置列表失败：", err);
      throw err;
    });
};

// 更新单个房源的分账配置
export const updateHouseSplitConfig = (houseId, params) => {
  console.log("更新房源分账配置：房源ID=", houseId, "参数：", params);
  return http.post(`/api/admin/split-config/houses/${houseId}`, params)
    .then(res => {
      console.log("更新房源分账配置成功响应：", res);
      return res;
    })
    .catch(err => {
      console.error("更新房源分账配置失败：", err);
      throw err;
    });
};

// 汇付管理相关接口
// 获取汇付账号列表（管理端）
export const getHuifuAccountList = (search = '', page = 1, pageSize = 20) => {
  console.log("查询汇付账号列表，搜索关键词：", search, "页码：", page, "每页数量：", pageSize);
  return http.get('/api/admin/huifu/accounts', {
    params: {
      search: search,
      page: page,
      page_size: pageSize
    }
  })
    .then(res => {
      console.log("查询汇付账号列表成功响应：", res);
      return res;
    })
    .catch(err => {
      console.error("查询汇付账号列表失败：", err);
      throw err;
    });
};

// 获取汇付银行卡列表（管理端）
export const getHuifuBankCardList = (search = '', page = 1, pageSize = 20) => {
  console.log("查询汇付银行卡列表，搜索关键词：", search, "页码：", page, "每页数量：", pageSize);
  return http.get('/api/admin/huifu/bank-cards', {
    params: {
      search: search,
      page: page,
      page_size: pageSize
    }
  })
    .then(res => {
      console.log("查询汇付银行卡列表成功响应：", res);
      return res;
    })
    .catch(err => {
      console.error("查询汇付银行卡列表失败：", err);
      throw err;
    });
};

// 新增汇付账号
export const addHuifuAccount = (data) => {
  return http.post('/api/admin/huifu/accounts', data)
    .then(res => {
      console.log("新增汇付账号成功响应：", res);
      return res;
    })
    .catch(err => {
      console.error("新增汇付账号失败：", err);
      throw err;
    });
};

// 更新汇付账号
export const updateHuifuAccount = (id, data) => {
  return http.put(`/api/admin/huifu/accounts/${id}`, data)
    .then(res => {
      console.log("更新汇付账号成功响应：", res);
      return res;
    })
    .catch(err => {
      console.error("更新汇付账号失败：", err);
      throw err;
    });
};

// 删除汇付账号
export const deleteHuifuAccount = (id) => {
  return http.delete(`/api/admin/huifu/accounts/${id}`)
    .then(res => {
      console.log("删除汇付账号成功响应：", res);
      return res;
    })
    .catch(err => {
      console.error("删除汇付账号失败：", err);
      throw err;
    });
};

// 新增汇付银行卡
export const addHuifuBankCard = (data) => {
  return http.post('/api/admin/huifu/bank-cards', data)
    .then(res => {
      console.log("新增汇付银行卡成功响应：", res);
      return res;
    })
    .catch(err => {
      console.error("新增汇付银行卡失败：", err);
      throw err;
    });
};

// 更新汇付银行卡
export const updateHuifuBankCard = (id, data) => {
  return http.put(`/api/admin/huifu/bank-cards/${id}`, data)
    .then(res => {
      console.log("更新汇付银行卡成功响应：", res);
      return res;
    })
    .catch(err => {
      console.error("更新汇付银行卡失败：", err);
      throw err;
    });
};

/** 汇付接口工具：查询用户汇付配置（管理端） */
export const huifuToolsQueryUser = (huifuId) => {
  return http.post('/api/admin/huifu/tools/query-user', { huifu_id: huifuId });
};

/** 汇付接口工具：改为仅手动提现（管理端） */
export const huifuToolsSetManualWithdraw = (huifuId) => {
  return http.post('/api/admin/huifu/tools/set-manual-withdraw', { huifu_id: huifuId });
};

/** 汇付接口工具：查询提现记录（管理端，自动结算+手动出款） */
export const huifuToolsQueryWithdrawRecords = (payload) => {
  return http.post('/api/admin/huifu/tools/query-withdraw-records', payload);
};

/** 汇付接口工具：查询账户余额（管理端） */
export const huifuToolsQueryBalance = (huifuId) => {
  return http.post('/api/admin/huifu/tools/query-balance', { huifu_id: huifuId });
};

/** 管理端：手动检查并执行今日需出账的月度账单 */
export const adminRunTodayBillCron = () => {
  return http.post('/api/admin/bill-cron/run-today');
};

// 删除汇付银行卡
export const deleteHuifuBankCard = (id) => {
  return http.delete(`/api/admin/huifu/bank-cards/${id}`)
    .then(res => {
      console.log("删除汇付银行卡成功响应：", res);
      return res;
    })
    .catch(err => {
      console.error("删除汇付银行卡失败：", err);
      throw err;
    });
};

/** 下载汇付账号批量导入模板（本地 CSV） */
export const downloadHuifuAccountTemplate = async () => {
  downloadHuifuAccountImportTemplateCsv();
  return { success: true };
};

/** 汇付账号批量导入（CSV，字段名与模板首行一致） */
export const batchImportHuifuAccounts = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return http
    .post('/api/admin/huifu/accounts/batch-import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then((res) => {
      console.log('汇付账号批量导入响应：', res);
      return res;
    })
    .catch((err) => {
      console.error('汇付账号批量导入失败：', err);
      throw err;
    });
};

/** 下载汇付银行卡批量导入模板（本地 CSV） */
export const downloadHuifuBankCardTemplate = async () => {
  downloadHuifuBankCardImportTemplateCsv();
  return { success: true };
};

/** 汇付银行卡批量导入（CSV） */
export const batchImportHuifuBankCards = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return http
    .post('/api/admin/huifu/bank-cards/batch-import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then((res) => {
      console.log('汇付银行卡批量导入响应：', res);
      return res;
    })
    .catch((err) => {
      console.error('汇付银行卡批量导入失败：', err);
      throw err;
    });
};

// ========== 招租二维码管理 ==========
export const getRentQRCodes = (opts = {}) => {
  const page = Number(opts.page) > 0 ? Number(opts.page) : 1;
  const page_size = Number(opts.page_size) > 0 ? Number(opts.page_size) : 20;
  return http.get("/api/admin/rent-qr-codes", {
    params: { page, page_size, search: opts.search || "" },
  });
};

export const updateRentQRCode = (id, data) =>
  http.put(`/api/admin/rent-qr-codes/${id}`, data);

export const createNextRentQRCode = () =>
  http.post("/api/admin/rent-qr-codes/next");

export const generateRentQRCode = (id) =>
  http.post(`/api/admin/rent-qr-codes/${id}/generate-qrcode`);

export const fetchRentQRCodeBlob = async (id) => {
  const token = localStorage.getItem("admin_token");
  const baseURL = http.defaults.baseURL || "";
  const url = `${baseURL}/api/admin/rent-qr-codes/${id}/download-qrcode`;
  const resp = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!resp.ok) {
    let msg = "下载失败";
    try {
      const errBody = await resp.json();
      msg = errBody.msg || errBody.error || msg;
    } catch (e) {
      /* ignore */
    }
    throw new Error(msg);
  }
  return resp.blob();
};

export const downloadRentQRCode = async (id, code) => {
  const blob = await fetchRentQRCodeBlob(id);
  const filename = `${String(code || id).trim() || "qrcode"}.png`;
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
};

// ========== 待办管理 ==========
export const getAdminTodos = (page = 1, pageSize = 20) =>
  http.get("/api/admin/todos", { params: { page, page_size: pageSize } });
export const createAdminTodo = (data) => http.post("/api/admin/todos", data);
export const updateAdminTodoStatus = (id, data) =>
  http.patch(`/api/admin/todos/${id}/status`, data);
export const deleteAdminTodo = (id) => http.delete(`/api/admin/todos/${id}`);
export const uploadAdminTodoImage = (file) => {
  const formData = new FormData();
  formData.append("file", file);
  return http.post("/api/admin/todos/upload-image", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

// ========== 角色管理（仅超管） ==========
export const getAdminRoles = () => http.get("/api/admin/roles");
export const createAdminRole = (data) => http.post("/api/admin/roles", data);
export const updateAdminRole = (id, data) => http.put(`/api/admin/roles/${id}`, data);
export const deleteAdminRole = (id) => http.delete(`/api/admin/roles/${id}`);
export const getAdminRoleMenuOptions = () => http.get("/api/admin/roles/menu-options");
export const getAdminRoleMembers = (roleId) =>
  http.get("/api/admin/roles/members", { params: { role_id: roleId } });
export const addAdminRoleMember = (data) => http.post("/api/admin/roles/members", data);
export const deleteAdminRoleMember = (memberId) =>
  http.delete(`/api/admin/roles/members/${memberId}`);
export const updateAdminRolePermissions = (id, permissions) =>
  http.put(`/api/admin/roles/${id}/permissions`, { permissions });

export default http;

